import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "const/axios";

export const reminderFilterOptions = [
  { value: "has_all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
];

export const visaExpiringDaysOptions = [
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "30 days" },
  { value: 60, label: "60 days" },
  { value: 90, label: "90 days" },
  { value: 120, label: "120 days" },
  { value: 180, label: "180 days/6months" },
  { value: 365, label: "365 days/1year" },
  { value: 540, label: "540 days/1.5years" },
  { value: 720, label: "720 days/2years" },
  { value: 900, label: "900 days/2.5years" },
  { value: 1080, label: "1080 days/3years" },
  { value: 1260, label: "1260 days/3.5years" },
  { value: 1440, label: "1440 days/4years" },
  { value: 1620, label: "1620 days/4.5years" },
];

export const progressStageOptions = [
  { value: "LOST", label: "Lost" },
  { value: "COLD", label: "Cold" },
  { value: "WARM", label: "Warm" },
  { value: "HOT", label: "Hot" },
];

// Generate months array
export const months = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

export const DEFAULT_CLIENT_SORT_BY = "visaExpiry";
export const DEFAULT_CLIENT_SORT_DIRECTION = "ASC";

export const sortOrderOptions = [
  { value: "ASC", label: "Ascending" },
  { value: "DESC", label: "Descending" },
];

export const sortByOptions = [
  { value: "visaExpiry", label: "Visa Expiry" },
  { value: "name", label: "Name" },
  { value: "email", label: "Email" },
  { value: "number", label: "Number" },
  { value: "passportNumber", label: "Passport Number" },
  { value: "visaStatus", label: "Visa Status" },
  { value: "caseOfficer", label: "Case Officer" },
  { value: "isConverted", label: "Converted" },
  { value: "createdDate", label: "Created Date" },
];

const getPaginatedClientDataAPI = async (params = {}) => {
  const { pageIndex = 0, pageSize = 20 } = params;
  const queryParams = new URLSearchParams({
    active: "Y",
    page: pageIndex + 1, // API expects 1-based indexing
    size: pageSize,
    ...(params?.searchText && { search: params?.searchText }),
    ...(params?.caseOfficer && { caseOfficer: params?.caseOfficer }),
    ...(params?.createdMonth && { createdMonth: params?.createdMonth }),
    ...(params?.createdYear && { createdYear: params?.createdYear }),
    ...(params?.sortBy && { sortBy: params?.sortBy }),
    ...(params?.sortDirection && { sortDirection: params?.sortDirection }),
    ...(params?.progressStage && { progressStage: params?.progressStage }),
    ...(params?.hasReminders === true && { hasReminders: true }),
    ...(params?.hasReminders === true &&
      params?.reminderStatus &&
      params?.reminderStatus !== "all" && { reminderStatus: params?.reminderStatus }),
    ...(params?.visaExpiringDays && { visaExpiringDays: params?.visaExpiringDays }),
    ...(params?.visaExpiringFrom && { visaExpiringFrom: params?.visaExpiringFrom }),
    ...(params?.visaExpiringTo && { visaExpiringTo: params?.visaExpiringTo }),
  });

  const res = await axios.get(`clients/page?${queryParams}`);
  const activeClients = res?.data?.data?.content?.filter((client) => client?.active === "Y") || [];

  return {
    ...res?.data?.data,
    content: [...activeClients],
  };
};

const getClientDataAPI = async (params = {}) => {
  const res = await axios.get(`clients`, {
    params: {
      ...(params?.visaExpiringDays ? { visaExpiringDays: params.visaExpiringDays } : {}),
    },
  });
  return res?.data?.data?.filter((client) => client?.active === "Y") ?? [];
};

const getArchivedClientAPI = async () => {
  const res = await axios.get(`clients/?active=N`);
  return res?.data?.data;
};

export const useClientById = (clientId, options = {}) =>
  useQuery({
    queryKey: ["client-by-id", clientId],
    queryFn: async () => {
      const res = await axios.get(`clients/${clientId}`);
      return res?.data?.data ?? null;
    },
    enabled: Boolean(clientId),
    refetchOnWindowFocus: false,
    ...options,
  });

export const usePaginatedClients = (pagination, options = {}) => {
  return useQuery({
    queryKey: ["paginatedClient"],
    queryFn: () => getPaginatedClientDataAPI(pagination),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: false,
    ...options,
  });
};

export const useClients = (options = {}) => {
  return useQuery({
    queryKey: ["client"],
    queryFn: getClientDataAPI,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 0,
    cacheTime: 0,
    enabled: false,
    ...options,
  });
};

export const useArchivedClients = (options = {}) => {
  return useQuery({
    queryKey: ["archivedClient"],
    queryFn: getArchivedClientAPI,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const createRefetchPaginatedClients = (
  queryClient,
  currentPagination,
  setPagination,
  refetchQuery
) => {
  return (params = {}) => {
    // Accept API-style page/size aliases used by callers
    const { page, size, ...rest } = params;
    const normalized = {
      ...rest,
      ...(page !== undefined && { pageIndex: page }),
      ...(size !== undefined && { pageSize: size }),
    };
    const mergedParams = { ...currentPagination, ...normalized };

    if (Object.keys(normalized).length > 0) {
      setPagination((prev) => ({ ...prev, ...normalized }));
    }

    if (Object.keys(normalized).length === 0) {
      return refetchQuery();
    }

    return queryClient.fetchQuery({
      queryKey: ["paginatedClient"],
      queryFn: () => getPaginatedClientDataAPI(mergedParams),
    });
  };
};

export const getPaginatedClientData = getPaginatedClientDataAPI;
export const getClientData = getClientDataAPI;
export const getArchivedClient = getArchivedClientAPI;
